import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { buildMeetingFlowGraph } from "./buildMeetingFlowGraph";
import { meetingEdgeTypes, meetingNodeTypes } from "./reactFlowTypes";
import type {
  LivestreamNodeData,
  MeetingRouteNodeData,
  StartAutomationNodeData,
} from "./types";

type MeetingCheckoutOnSuccessAction =
  | { type: "GoTo"; route: string }
  | { type: "StartAutomation"; automationId: string };

function getPageBaseUrl(hostname: string | null): string {
  if (!hostname) return window.location.origin;
  if (hostname === "localhost") return "http://localhost:3002";
  return `https://${hostname}`;
}

function resolveStartAutomationId(
  onSuccessActions?: MeetingCheckoutOnSuccessAction[],
): string | undefined {
  const action = onSuccessActions?.find(
    (entry): entry is { type: "StartAutomation"; automationId: string } =>
      entry.type === "StartAutomation" && Boolean(entry.automationId?.trim()),
  );
  return action?.automationId?.trim() || undefined;
}

export function ProductTypeMeetingEditFlowTab({
  productId,
  accessOnSuccessActions,
  shopOnSuccessActions,
  onFlowChange,
}: {
  productId: string;
  accessOnSuccessActions?: MeetingCheckoutOnSuccessAction[];
  shopOnSuccessActions?: MeetingCheckoutOnSuccessAction[];
  onFlowChange?: () => void;
}) {
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [accessAutomationId, setAccessAutomationId] = useState(() =>
    resolveStartAutomationId(accessOnSuccessActions),
  );
  const [shopAutomationId, setShopAutomationId] = useState(() =>
    resolveStartAutomationId(shopOnSuccessActions),
  );
  const [accessIsSaving, setAccessIsSaving] = useState(false);
  const [shopIsSaving, setShopIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<
    Node<MeetingRouteNodeData | LivestreamNodeData | StartAutomationNodeData>[]
  >([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  useEffect(() => {
    setAccessAutomationId(resolveStartAutomationId(accessOnSuccessActions));
  }, [accessOnSuccessActions]);

  useEffect(() => {
    setShopAutomationId(resolveStartAutomationId(shopOnSuccessActions));
  }, [shopOnSuccessActions]);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const baseUrl = useMemo(
    () => getPageBaseUrl(customDomainHostname),
    [customDomainHostname],
  );
  const defaultViewport = useMemo(() => ({ x: 40, y: 40, zoom: 1 }), []);

  const saveMeetingCheckoutAutomation = useCallback(
    async ({
      target,
      nextAutomationId,
      previousAutomationId,
      setAutomationId,
      setIsSaving,
    }: {
      target: "access" | "shop";
      nextAutomationId: string;
      previousAutomationId: string | undefined;
      setAutomationId: (value: string | undefined) => void;
      setIsSaving: (value: boolean) => void;
    }) => {
      setAutomationId(nextAutomationId.trim() || undefined);
      setIsSaving(true);
      setSaveError(null);

      const onSuccessActions = nextAutomationId.trim()
        ? [{ type: "StartAutomation" as const, automationId: nextAutomationId.trim() }]
        : [];

      try {
        const response = await fetch(`/api/products/${productId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            meeting: {
              [target]: {
                checkout: { onSuccessActions },
              },
            },
          }),
        });
        const data = (await response.json()) as { error?: string };
        if (!response.ok) {
          setAutomationId(previousAutomationId);
          setSaveError(data.error || "Failed to save automation");
          return;
        }
        onFlowChange?.();
      } catch (error) {
        console.error("Failed to save meeting checkout automation:", error);
        setAutomationId(previousAutomationId);
        setSaveError("An error occurred while saving");
      } finally {
        setIsSaving(false);
      }
    },
    [onFlowChange, productId],
  );

  const handleAccessAutomationChange = useCallback(
    (nextAutomationId: string) => {
      void saveMeetingCheckoutAutomation({
        target: "access",
        nextAutomationId,
        previousAutomationId: accessAutomationId,
        setAutomationId: setAccessAutomationId,
        setIsSaving: setAccessIsSaving,
      });
    },
    [accessAutomationId, saveMeetingCheckoutAutomation],
  );

  const handleShopAutomationChange = useCallback(
    (nextAutomationId: string) => {
      void saveMeetingCheckoutAutomation({
        target: "shop",
        nextAutomationId,
        previousAutomationId: shopAutomationId,
        setAutomationId: setShopAutomationId,
        setIsSaving: setShopIsSaving,
      });
    },
    [saveMeetingCheckoutAutomation, shopAutomationId],
  );

  useEffect(() => {
    const graph = buildMeetingFlowGraph({
      productId,
      baseUrl,
      accessAutomationId,
      shopAutomationId,
      accessIsSaving,
      shopIsSaving,
      onAccessAutomationChange: handleAccessAutomationChange,
      onShopAutomationChange: handleShopAutomationChange,
    });
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }, [
    accessAutomationId,
    accessIsSaving,
    baseUrl,
    handleAccessAutomationChange,
    handleShopAutomationChange,
    productId,
    shopAutomationId,
    shopIsSaving,
  ]);

  return (
    <div className="space-y-2">
      {saveError ? (
        <p className="text-sm text-destructive">{saveError}</p>
      ) : null}
      <div className="h-[480px] rounded-lg border border-gray-200 overflow-hidden relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={meetingNodeTypes}
          edgeTypes={meetingEdgeTypes}
          defaultViewport={defaultViewport}
          nodesConnectable={false}
          nodesDraggable={false}
          elementsSelectable={false}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          zoomOnScroll
          panOnScroll
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  );
}
